import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getSession, onAuthChange, signIn, signOut, usingDemoAuth } from "lib/auth";

const AuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = still checking
  const [error, setError] = useState("");

  useEffect(() => {
    getSession().then(setSession);
    const unsubscribe = onAuthChange(setSession);
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      await signIn(email, password);
      setSession(await getSession());
      return true;
    } catch (err) {
      setError(err.message || "Could not sign in.");
      return false;
    }
  };

  const logout = async () => {
    await signOut();
    setSession(null);
  };

  const value = useMemo(
    () => ({
      session,
      isAuthenticated: Boolean(session),
      isLoading: session === undefined,
      error,
      login,
      logout,
      usingDemoAuth,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AdminAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAdminAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
